import { useRef, useEffect } from "react";

export function useHorizontalScrollDrag() {
  const ref = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const hasMoved = useRef(false); // preventing click event when dragging
  const startX = useRef(0);
  const scrollLeft = useRef(0);
  const originalScrollSnapType = useRef<string>("");

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const getPageX = (e: MouseEvent | TouchEvent): number => {
      if ("touches" in e) {
        const touch = e.touches[0] ?? e.changedTouches[0];
        return touch?.pageX ?? 0;
      }
      return e.pageX;
    };

    const startDrag = (pageX: number) => {
      isDragging.current = true;
      hasMoved.current = false;
      startX.current = pageX - el.offsetLeft;
      scrollLeft.current = el.scrollLeft;
      el.style.cursor = "grabbing";
      el.style.userSelect = "none";
      originalScrollSnapType.current = el.style.scrollSnapType;
      el.style.scrollSnapType = "none";
    };

    const moveDrag = (pageX: number) => {
      if (!isDragging.current) return;
      const x = pageX - el.offsetLeft;
      const walk = (x - startX.current) * 2;
      if (Math.abs(walk) > 5) {
        hasMoved.current = true;
      }
      el.scrollLeft = scrollLeft.current - walk;
    };

    const endDrag = (e?: MouseEvent | TouchEvent) => {
      if (isDragging.current && hasMoved.current) {
        e?.preventDefault();
        e?.stopPropagation();
      }
      const wasDragging = isDragging.current && hasMoved.current;
      isDragging.current = false;
      el.style.cursor = "grab";
      el.style.userSelect = "";
      el.style.scrollSnapType = originalScrollSnapType.current;
      if (wasDragging) {
        const handleClickCapture = (e: MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          el.removeEventListener("click", handleClickCapture, true);
        };
        el.addEventListener("click", handleClickCapture, true);
        setTimeout(() => {
          el.removeEventListener("click", handleClickCapture, true);
        }, 0);
      }
      hasMoved.current = false;
    };

    const handleMouseDown = (e: MouseEvent) => {
      startDrag(getPageX(e));
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      e.preventDefault();
      moveDrag(getPageX(e));
    };

    const handleMouseUp = (e: MouseEvent) => {
      endDrag(e);
    };

    const handleTouchStart = (e: TouchEvent) => {
      startDrag(getPageX(e));
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging.current) return;
      e.preventDefault();
      moveDrag(getPageX(e));
    };

    const handleTouchEnd = (e: TouchEvent) => {
      endDrag(e);
    };

    const handleMouseLeave = () => {
      endDrag();
    };

    const handleDragStart = (e: DragEvent) => e.preventDefault();

    el.style.cursor = "grab";
    el.addEventListener("mousedown", handleMouseDown);
    el.addEventListener("mousemove", handleMouseMove);
    el.addEventListener("mouseup", handleMouseUp);
    el.addEventListener("mouseleave", handleMouseLeave);
    el.addEventListener("touchstart", handleTouchStart, { passive: true });
    el.addEventListener("touchmove", handleTouchMove, { passive: false });
    el.addEventListener("touchend", handleTouchEnd);
    el.addEventListener("touchcancel", handleTouchEnd);
    el.addEventListener("dragstart", handleDragStart);

    return () => {
      el.removeEventListener("mousedown", handleMouseDown);
      el.removeEventListener("mousemove", handleMouseMove);
      el.removeEventListener("mouseup", handleMouseUp);
      el.removeEventListener("mouseleave", handleMouseLeave);
      el.removeEventListener("touchstart", handleTouchStart);
      el.removeEventListener("touchmove", handleTouchMove);
      el.removeEventListener("touchend", handleTouchEnd);
      el.removeEventListener("touchcancel", handleTouchEnd);
      el.removeEventListener("dragstart", handleDragStart);
    };
  }, []);

  return ref;
}
